<template>
  <div>
    <b-card class="products-card">
      <b-row>
        <b-col xl="4" lg="6">
          <router-link
            :to="{ name: 'AssignmentTermsDashboard' }"
            class="navbar-brand"
            >Back to search</router-link
          >
        </b-col>
      </b-row>
      <b-row>
        <b-col xl="4" lg="6">
          <h4>{{ this.vendor.label }}</h4>
          <p class="mt-2 font-italic">
            You can only create Assignment Terms for this Partner in
            <a :href="this.vendor.salesforceLink" target="_blank">
              Salesforce
            </a>
            via a Contract Term
          </p>
          <p class="text-danger">
            {{ warning }}
          </p>
        </b-col>
        <b-col xl="4" lg="6">
          <v-select v-model="product" :options="otherProducts" class="mb-3">
            <template slot="option" slot-scope="option">
              <div class="d-center">{{ option.label }}</div>
            </template>
            <template slot="selected-option" slot-scope="option">
              <div class="selected d-center">{{ option.label }}</div>
            </template>
          </v-select>
        </b-col>
      </b-row>
    </b-card>
    <b-card
      title="Assignment Term Quick Links"
      class="b-card-space"
      v-if="productId"
    >
      <b-nav pills>
        <b-nav-item
          v-for="assignmentTerm in assignmentTerms"
          :key="assignmentTerm.Id"
        >
          <a v-scroll-to="'#at-' + assignmentTerm.id" href="">
            <font-awesome-icon icon="arrow-down" />{{
              displayTitle(assignmentTerm)
            }}</a
          >
        </b-nav-item>
        <div class="mb-3" v-if="affiliatedGroupId != null">
          <b-nav-item>
            <b-link
              variant="link"
              size="sm"
              :to="{
                name: 'AffiliatedGroupDetails',
                params: { id: this.affiliatedGroupId }
              }"
            >
              Mother account
            </b-link>
          </b-nav-item>
        </div>
        <div class="mb-3">
          <b-nav-item>
            <b-link @click="redirectToDistricts" variant="link" size="sm">
              Districts
            </b-link>
          </b-nav-item>
        </div>
        <div class="mb-3">
          <b-nav-item>
            <b-link @click="redirectToBackend" variant="link" size="sm">
              Vendor
            </b-link>
          </b-nav-item>
        </div>
        <div class="mb-3" v-if="this.vendor.salesforceLink">
          <b-nav-item>
            <b-link @click="redirectToSalesforce" variant="link" size="sm">
              Salesforce
            </b-link>
          </b-nav-item>
        </div>
      </b-nav>
    </b-card>

    <div v-for="assignmentTerm in assignmentTerms" :key="assignmentTerm.id">
      <AssignmentTermItem
        v-bind="assignmentTerm"
        v-bind:vendorId="vendor.id"
        v-bind:vendorUuid="vendor.uuid"
        v-bind:productId="product.id"
        v-bind:productUuid="product.uuid"
        v-bind:refreshFunction="refreshAfterUpdate"
        v-bind:updateForm="true"
      />
    </div>
  </div>
</template>

<script>
import AssignmentTermItem from '@/components/AssignmentTermItem'
import AssignmentTermsProvider from '@/services/AssignmentTermsProvider'

export default {
  name: 'AssignmentTermsList',
  data () {
    return {
      assignmentTerms: [],
      otherProducts: [],
      vendor: {},
      product: {},
      productId: null,
      affiliatedGroupId: null,
      districtsLink: null,
      backendLink: null,
      assignmentTermId: null,
      warning: null
    }
  },
  created () {
    this.vendor = { id: this.$route.params.vendorId }
    this.product = { id: this.$route.params.productId }
    this.productId = this.$route.params.productId
    const routeAssignmentTermId = this.$route.params.assignmentTermId
    const queryAssignmentTermId = this.$route.query.at_id
    const assignmentTermId = routeAssignmentTermId || queryAssignmentTermId
    this.assignmentTermId = assignmentTermId ? parseInt(assignmentTermId) : null
    this.fetchAssignmentTerms()
  },
  updated () {
    if (this.assignmentTermId) {
      let el = `at-${this.assignmentTermId}`
      if (document.getElementById(el)) {
        this.$scrollTo(`#${el}`)
      }
    }
  },
  components: {
    AssignmentTermItem
  },
  computed: {
    isUserAdmin () {
      return this.$store.getters.isAdmin
    }
  },
  methods: {
    fetchAssignmentTerms () {
      if (this.productId === null) {
        this.assignmentTerms = []
        return
      }
      AssignmentTermsProvider.all({
        vendorId: this.vendor.id,
        productId: this.productId
      }).then(resp => {
        this.assignmentTerms = resp.body.assignment_terms
        this.affiliatedGroupId = resp.body.affiliated_group_id
        this.districtsLink = resp.body.districts_link
        this.backendLink = resp.body.backend_link
        this.otherProducts = resp.body.other_products.map(product => {
          return { id: product.id, label: product.name, uuid: product.uuid }
        })
        this.vendor = {
          id: resp.body.vendor_id,
          uuid: resp.body.vendor_uuid,
          label: resp.body.vendor_name,
          salesforceLink: resp.body.vendor_salesforce_link
        }
        this.product = this.otherProducts.find(product => {
          return this.isSelectedProduct(product)
        })
      }).catch(error => {
        this.warning = (error.body && error.body.errors && error.body.errors[0]) || error.statusText || 'Error getting Assignment Terms'
      })
    },
    refreshAfterUpdate (assignmentTerm) {
      const index = this.assignmentTerms.findIndex(i => {
        return i.id === assignmentTerm.id
      })
      this.assignmentTerms[index] = Object.assign(
        this.assignmentTerms[index],
        assignmentTerm
      )
    },
    redirectToDistricts () {
      // I'm using window.open, because there are issues with opening links in new tabs in vuejs.
      window.open(this.districtsLink, '_blank')
    },

    displayTitle (assignmentTerm) {
      if (assignmentTerm.name) {
        return `${assignmentTerm.name} (#${assignmentTerm.id})`
      } else {
        return '# ' + assignmentTerm.id
      }
    },
    redirectToBackend () {
      // I'm using window.open, because there are issues with opening links in new tabs in vuejs.
      window.open(this.backendLink, '_blank')
    },
    redirectToSalesforce () {
      // I'm using window.open, because there are issues with opening links in new tabs in vuejs.
      window.open(this.vendor.salesforceLink, '_blank')
    },
    isSelectedProduct (product) {
      return (
        product.id === parseInt(this.productId) ||
        product.uuid === this.productId
      )
    }
  },
  watch: {
    product (newObject) {
      if (this.isSelectedProduct(newObject)) {
        return
      }
      this.$router.push({
        name: 'AssignmentTermsList',
        params: { vendorId: this.vendor.id, productId: newObject.id },
        query: this.$route.query
      })
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.products-card {
  margin-top: 10px;
  margin-bottom: 10px;
}

.b-card-space {
  margin-bottom: 10px;
  margin-top: 10px;
}
</style>
